import {
  PersonOutlineOutlined,
  CreditCardOutlined,
  PublicOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material';

import type { Mitigation, RiskCategory } from '@/types/riskAssessment';

export const riskCategories: RiskCategory[] = [
  {
    id: 'customer',
    name: 'Riesgo del Cliente',
    icon: <PersonOutlineOutlined fontSize="inherit" />,
    description: 'Evaluación del perfil y características del cliente',
    factors: [
      {
        id: 'customer-type',
        name: 'Tipo de Cliente',
        description: 'Naturaleza jurídica del cliente',
        options: [
          {
            id: 'individual',
            label: 'Persona Física',
            description: 'Cliente individual',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'sme',
            label: 'PYME',
            description: 'Pequeña o mediana empresa',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'large-corp',
            label: 'Empresa Grande',
            description: 'Gran corporación',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
      {
        id: 'kyc-level',
        name: 'Nivel de Conocimiento del Cliente',
        description: 'Profundidad de la información disponible',
        options: [
          {
            id: 'full-kyc',
            label: 'KYC Completo',
            description: 'Documentación completa y verificada',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'basic-kyc',
            label: 'KYC Básico',
            description: 'Documentación básica',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'minimal-kyc',
            label: 'KYC Mínimo',
            description: 'Información limitada',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
      {
        id: 'pep-status',
        name: 'Persona Políticamente Expuesta (PEP)',
        description: 'Exposición política del cliente',
        options: [
          {
            id: 'no-pep',
            label: 'No es PEP',
            description: 'Sin exposición política',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'related-pep',
            label: 'Relacionado con PEP',
            description: 'Familiar o asociado de PEP',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'is-pep',
            label: 'Es PEP',
            description: 'Persona políticamente expuesta',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
    ],
  },
  {
    id: 'product',
    name: 'Riesgo del Producto/Servicio',
    icon: <CreditCardOutlined fontSize="inherit" />,
    description: 'Evaluación de los productos y servicios utilizados',
    factors: [
      {
        id: 'product-type',
        name: 'Tipo de Producto',
        description: 'Productos financieros utilizados',
        options: [
          {
            id: 'basic-account',
            label: 'Cuenta Básica',
            description: 'Cuenta de nómina estándar',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'payroll-advances',
            label: 'Adelantos de Nómina',
            description: 'Servicio de adelantos',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'high-value',
            label: 'Productos de Alto Valor',
            description: 'Productos con alta exposición',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
      {
        id: 'transaction-volume',
        name: 'Volumen Transaccional',
        description: 'Volumen mensual de transacciones',
        options: [
          {
            id: 'low-volume',
            label: 'Bajo (< $100k MXN/mes)',
            description: 'Volumen bajo de transacciones',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'medium-volume',
            label: 'Medio ($100k - $1M MXN/mes)',
            description: 'Volumen medio de transacciones',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'high-volume',
            label: 'Alto (> $1M MXN/mes)',
            description: 'Volumen alto de transacciones',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
      {
        id: 'payment-methods',
        name: 'Métodos de Pago',
        description: 'Canales de pago utilizados',
        options: [
          {
            id: 'standard-methods',
            label: 'Métodos Estándar',
            description: 'SPEI, transferencias bancarias',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'mixed-methods',
            label: 'Métodos Mixtos',
            description: 'Combinación de canales',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'high-risk-methods',
            label: 'Métodos de Alto Riesgo',
            description: 'Efectivo, criptomonedas',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
    ],
  },
  {
    id: 'geography',
    name: 'Riesgo Geográfico',
    icon: <PublicOutlined fontSize="inherit" />,
    description: 'Evaluación de ubicación y alcance geográfico',
    factors: [
      {
        id: 'location',
        name: 'Ubicación del Cliente',
        description: 'Ubicación geográfica principal',
        options: [
          {
            id: 'low-risk-location',
            label: 'Zona de Bajo Riesgo',
            description: 'Áreas urbanas principales',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'medium-risk-location',
            label: 'Zona de Riesgo Medio',
            description: 'Áreas con riesgo moderado',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'high-risk-location',
            label: 'Zona de Alto Riesgo',
            description: 'Áreas con alta incidencia criminal',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
      {
        id: 'international-operations',
        name: 'Operaciones Internacionales',
        description: 'Alcance de operaciones transfronterizas',
        options: [
          {
            id: 'domestic-only',
            label: 'Solo Nacional',
            description: 'Operaciones únicamente en México',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'limited-international',
            label: 'Internacional Limitado',
            description: 'Algunos países de bajo riesgo',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'high-risk-countries',
            label: 'Países de Alto Riesgo',
            description: 'Jurisdicciones no cooperantes',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
    ],
  },
  {
    id: 'transaction',
    name: 'Riesgo Transaccional',
    icon: <ReceiptLongOutlined />,
    description: 'Evaluación de patrones y comportamiento transaccional',
    factors: [
      {
        id: 'transaction-pattern',
        name: 'Patrón de Transacciones',
        description: 'Consistencia y predictibilidad',
        options: [
          {
            id: 'predictable',
            label: 'Predecible',
            description: 'Transacciones consistentes y esperadas',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'variable',
            label: 'Variable',
            description: 'Cierta variabilidad en transacciones',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'erratic',
            label: 'Errático',
            description: 'Patrones inconsistentes o inusuales',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
      {
        id: 'cash-intensity',
        name: 'Intensidad de Efectivo',
        description: 'Uso de efectivo en operaciones',
        options: [
          {
            id: 'low-cash',
            label: 'Bajo Uso de Efectivo',
            description: 'Principalmente transacciones electrónicas',
            riskLevel: 'low',
            score: 1,
          },
          {
            id: 'medium-cash',
            label: 'Uso Moderado de Efectivo',
            description: 'Mezcla de efectivo y electrónico',
            riskLevel: 'medium',
            score: 2,
          },
          {
            id: 'high-cash',
            label: 'Alto Uso de Efectivo',
            description: 'Predominantemente efectivo',
            riskLevel: 'high',
            score: 3,
          },
        ],
      },
    ],
  },
];

export const mitigations: Mitigation[] = [
  {
    id: 'enhanced-due-diligence',
    title: 'Debida Diligencia Reforzada',
    description:
      'Implementar procedimientos adicionales de verificación y documentación para clientes de alto riesgo.',
    priority: 'high',
    applicableFor: ['high'],
    category: 'customer',
  },
  {
    id: 'continuous-monitoring',
    title: 'Monitoreo Continuo',
    description:
      'Establecer sistemas de monitoreo en tiempo real para detectar actividades inusuales.',
    priority: 'high',
    applicableFor: ['medium', 'high'],
    category: 'transaction',
  },
  {
    id: 'transaction-limits',
    title: 'Límites Transaccionales',
    description: 'Implementar límites más estrictos en montos y frecuencia de transacciones.',
    priority: 'high',
    applicableFor: ['high'],
    category: 'product',
  },
  {
    id: 'senior-approval',
    title: 'Aprobación de Gerencia',
    description: 'Requerir aprobación de nivel superior para transacciones de alto riesgo.',
    priority: 'high',
    applicableFor: ['high'],
    category: 'customer',
  },
  {
    id: 'periodic-review',
    title: 'Revisión Periódica',
    description: 'Revisar y actualizar el perfil del cliente cada 6-12 meses.',
    priority: 'medium',
    applicableFor: ['medium', 'high'],
    category: 'customer',
  },
  {
    id: 'source-of-funds',
    title: 'Verificación de Origen de Fondos',
    description: 'Solicitar y verificar documentación sobre el origen de los fondos.',
    priority: 'high',
    applicableFor: ['high'],
    category: 'transaction',
  },
  {
    id: 'alert-system',
    title: 'Sistema de Alertas',
    description:
      'Configurar alertas automáticas para transacciones que excedan umbrales específicos.',
    priority: 'medium',
    applicableFor: ['medium', 'high'],
    category: 'transaction',
  },
  {
    id: 'geographic-restrictions',
    title: 'Restricciones Geográficas',
    description: 'Limitar operaciones con jurisdicciones de alto riesgo.',
    priority: 'high',
    applicableFor: ['high'],
    category: 'geography',
  },
  {
    id: 'reporting-protocol',
    title: 'Protocolo de Reportes',
    description: 'Establecer procedimientos claros para reportar actividades sospechosas.',
    priority: 'high',
    applicableFor: ['medium', 'high'],
    category: 'transaction',
  },
  {
    id: 'staff-training',
    title: 'Capacitación del Personal',
    description: 'Entrenar al equipo en identificación y manejo de riesgos específicos.',
    priority: 'medium',
    applicableFor: ['low', 'medium', 'high'],
    category: 'customer',
  },
];
